import { create } from 'zustand';
import { socket } from '../net/socket.js';
import {
  downsample,
  fromBase64,
  mulawDecode,
  mulawEncode,
  normalize,
  toBase64,
  VOICE_FORMAT,
  VOICE_RATE,
} from './clip.js';
import { sharedContext } from './sfx.js';

/**
 * Hold-to-talk. Press acquires the mic and records; release downsamples,
 * compands and sends one clip; the server relays it to everyone else at the
 * table, who hear it and see the speaker's pod light up.
 *
 * A clip, not a stream, on purpose: it rides the game's own JSON socket with
 * no second transport, survives any network the game itself survives, and can
 * never leave a mic open. The cost is walkie-talkie latency, which is the
 * honest register of the button.
 */

/** Server schema caps at 8s; stop there rather than have the relay refuse it. */
const MAX_MS = 8_000;
/** Below this it is a misfire on the button, not speech. */
const MIN_MS = 250;

interface VoiceState {
  ptt: 'idle' | 'live';
  /** Mic permission refused at the OS/browser level; the button says so. */
  denied: boolean;
  /** Seats currently heard, each until its clip's end. */
  speaking: { seat: number; until: number }[];
}

export const useVoice = create<VoiceState>(() => ({ ptt: 'idle', denied: false, speaking: [] }));

interface Recording {
  stream: MediaStream;
  source: MediaStreamAudioSourceNode;
  node: ScriptProcessorNode;
  sink: GainNode;
  chunks: Float32Array[];
  startedAt: number;
  tableId: string;
  timer: ReturnType<typeof setTimeout>;
}

let rec: Recording | null = null;
/** Bumped on every press AND release, so a release that lands while
 *  getUserMedia is still awaiting cancels that acquisition instead of leaving
 *  a mic recording with nobody holding the button. */
let pressSeq = 0;

export async function startTalk(tableId: string): Promise<void> {
  if (rec) return;
  const c = sharedContext();
  if (!c || typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;

  // Claimed synchronously, before ANY await: a release that lands during the
  // resume or the permission prompt bumps pressSeq, and this press must see
  // that and stand down — the alternative is a mic that records eight seconds
  // of the room for a tap the player believes did nothing.
  const my = ++pressSeq;
  if (c.state === 'suspended') await c.resume().catch(() => undefined);
  if (my !== pressSeq) return;

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch (e) {
    // Only an actual refusal parks the button; a busy mic or a dismissed
    // prompt is transient and the next press should simply try again.
    const name = e instanceof DOMException ? e.name : '';
    useVoice.setState({ ptt: 'idle', ...(name === 'NotAllowedError' || name === 'SecurityError' ? { denied: true } : {}) });
    return;
  }
  if (my !== pressSeq || rec) {
    for (const track of stream.getTracks()) track.stop();
    return;
  }

  const source = c.createMediaStreamSource(stream);
  /*
   * ScriptProcessor is deprecated, and is also the one capture node that runs
   * on every WebView this app ships in — AudioWorklet wants a separate module
   * served next to the bundle, which the Capacitor shells complicate for no
   * audible gain at phone quality. It only runs while the button is held.
   */
  const node = c.createScriptProcessor(4096, 1, 1);
  const sink = c.createGain();
  sink.gain.value = 0;
  const chunks: Float32Array[] = [];
  node.onaudioprocess = (e) => {
    chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
  };
  source.connect(node);
  node.connect(sink);
  sink.connect(c.destination);

  rec = {
    stream,
    source,
    node,
    sink,
    chunks,
    startedAt: Date.now(),
    tableId,
    timer: setTimeout(() => finish(true), MAX_MS),
  };
  useVoice.setState({ ptt: 'live', denied: false });
}

export function stopTalk(): void {
  finish(true);
}

export function cancelTalk(): void {
  finish(false);
}

function finish(send: boolean): void {
  pressSeq++;
  const r = rec;
  if (!r) return;
  rec = null;

  clearTimeout(r.timer);
  r.node.onaudioprocess = null;
  r.source.disconnect();
  r.node.disconnect();
  r.sink.disconnect();
  for (const track of r.stream.getTracks()) track.stop();
  useVoice.setState({ ptt: 'idle' });

  const ms = Date.now() - r.startedAt;
  if (!send || ms < MIN_MS) return;

  const c = sharedContext();
  if (!c) return;
  let length = 0;
  for (const chunk of r.chunks) length += chunk.length;
  const all = new Float32Array(length);
  let at = 0;
  for (const chunk of r.chunks) {
    all.set(chunk, at);
    at += chunk.length;
  }

  const slow = downsample(all, c.sampleRate);
  const level = normalize(slow.subarray(0, (MAX_MS / 1_000) * VOICE_RATE));
  // Nobody actually said anything — a held button over a quiet room is not a
  // message, and sending it would light this seat up for everyone.
  if (!level) return;
  const bytes = mulawEncode(level);
  if (bytes.length === 0) return;
  socket.send({
    t: 'voice.clip',
    tableId: r.tableId,
    format: VOICE_FORMAT,
    data: toBase64(bytes),
    ms: Math.max(250, Math.min(MAX_MS, ms)),
  });
}

/** Wire playback up once, before render — same contract as the sfx director. */
export function startVoice(): void {
  socket.on((m) => {
    if (m.t === 'voice.clip') playClip(m);
  });
  // A press whose release can no longer reach the button — the app went to
  // the background, the page is being torn down — is over, not still held.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') cancelTalk();
    });
    window.addEventListener('pagehide', () => cancelTalk());
  }
}

function playClip(m: { seat: number; format: string; data: string }): void {
  // Unknown future shapes are skipped quietly rather than played as noise.
  if (!m.format.startsWith('pcm-mulaw')) return;
  const c = sharedContext();
  if (!c || c.state !== 'running') return;

  let pcm: Float32Array<ArrayBuffer>;
  try {
    pcm = mulawDecode(fromBase64(m.data));
  } catch {
    return;
  }
  if (pcm.length === 0) return;

  const buffer = c.createBuffer(1, pcm.length, VOICE_RATE);
  buffer.copyToChannel(pcm, 0);
  const src = c.createBufferSource();
  src.buffer = buffer;
  // Not through the sfx master: see sharedContext(). A fixed 0.9 keeps voices
  // above the table sounds without clipping.
  const gain = c.createGain();
  gain.gain.value = 0.9;
  src.connect(gain);
  gain.connect(c.destination);
  src.start();

  markSpeaking(m.seat, (pcm.length / VOICE_RATE) * 1_000);
}

function markSpeaking(seat: number, ms: number): void {
  const now = Date.now();
  const until = now + ms;
  useVoice.setState((s) => ({
    speaking: [...s.speaking.filter((x) => x.seat !== seat && x.until > now), { seat, until }],
  }));
  setTimeout(() => {
    const at = Date.now();
    useVoice.setState((s) => ({ speaking: s.speaking.filter((x) => x.until > at) }));
  }, ms + 60);
}
